import { ParamSchemaType } from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { LeakageInput } from "@/lib/form/widgets/leakage"
import { posthog } from "@/lib/posthog"
import { MarketType, ProductFormData } from "@/packaging/lib/form"
import {
  DepartmentSchema,
  PackagingParamSchemaType,
} from "@/packaging/lib/schema"
import { api } from "@/services/base"
import useStore from "@/store"
import { DeptHistory } from "@/store/packaging"
import { HTTPError } from "ky"
import { useRef, useState } from "react"
import { useInterval } from "react-use"

export async function getAllDeptSchemas(): Promise<DepartmentSchema[]> {
  const res = await api.get<DepartmentSchema[]>("packaging/department-schemas")

  return res.json()
}

export interface StartPackagingShiftPayload {
  processId: string
  shiftId: string
}

export async function startPackagingShift(
  payload: StartPackagingShiftPayload
): Promise<void> {
  await api.post("packaging/v3/start-shift", {
    json: payload,
  })

  const store = useStore.getState()
  store.packaging.startShift(payload)
}

export async function discardPackagingShift() {
  const store = useStore.getState()

  try {
    const res = await api.get("packaging/v3/shift/discard")

    store.packaging.clearShift()

    return res
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err.response.status === 500) {
        store.packaging.clearShift()
      }
    }

    throw err
  }
}

export async function endPackagingShift() {
  const store = useStore.getState()

  try {
    const res = await api.get("packaging/v3/end-shift")

    store.session.endProcess(store.packaging.shift.processId)

    return res
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err.response.status === 500) {
        store.packaging.clearShift()
      }
    }

    throw err
  }
}

export interface SheetSubmissionParam {
  id: string
  value: string | number
  recordedAt: string
  remark?: string
}

export interface SheetSubmission {
  /**
   * Department ID
   */
  id: string
  machine: {
    id: string
    productId: string
    skuId: string
    market: MarketType
    status: FormParamStatus
    type: PackagingParamSchemaType
    parameters: SheetSubmissionParam[]
  }
}

export type SubmitSheetPayload = SheetSubmission[]

export async function submitSheet(
  data: ProductFormData,
  params: { deptId: string; machineId: string; sectionId: string }
): Promise<void> {
  const section = data.sections.find(
    (section) => section.id === params.sectionId
  )
  if (!section) {
    return
  }

  const selectedProductId = data.sections.at(0)?.parameters.at(1)?.value
  if (!selectedProductId) {
    return
  }

  const selectedSKUId = data.sections.at(0)?.parameters.at(2)?.value
  if (!selectedSKUId) {
    return
  }

  const market = data.sections.at(0)?.parameters.at(0)?.value
  if (!market) {
    return
  }

  const payload: SubmitSheetPayload = [
    {
      id: params.deptId,
      machine: {
        id: params.machineId,
        productId: selectedProductId,
        skuId: selectedSKUId,
        status: FormParamStatus.SUBMITTED,
        market: market.toUpperCase() as MarketType,
        type: section.type,
        parameters: section.parameters
          .map((param) => {
            let value: string = param.value
            if (Array.isArray(param.value)) {
              const leakages = (param.value as unknown as LeakageInput[]).map(
                (item) => ({ name: item.type, count: item.count })
              )
              value = JSON.stringify(leakages)
            }

            return {
              id: param.id,
              value: value,
              remark: param.remark,
              recordedAt: param.recordedAt,
            }
          })
          .filter(
            (param) =>
              typeof param.value === "string" && typeof param.id === "string"
          ),
      },
    },
  ]
  await api.post("packaging/v3/sheets/submit", {
    json: payload,
  })
}

export function useSubmitPackagingForms() {
  const isSubmitting = useRef(false)
  const retryCount = useRef(0)
  const [isEnabled, setIsEnabled] = useState(true)
  const packagingStore = useStore.use.packaging()
  const MAX_RETRIES = 10
  const MAX_TIMEOUT_MS = 30 * 1000

  useInterval(
    async () => {
      let productFormData: ProductFormData | undefined

      try {
        if (isSubmitting.current) {
          return
        }

        if (retryCount.current > MAX_RETRIES) {
          setIsEnabled(false)

          setTimeout(() => {
            setIsEnabled(true)
            retryCount.current = 0
          }, MAX_TIMEOUT_MS)

          return
        }

        isSubmitting.current = true

        const oldest = packagingStore.getNextSubmission()
        if (!oldest) {
          isSubmitting.current = false

          return
        }

        if (!oldest.sectionId) {
          packagingStore.dequeOldestSubmission()
          isSubmitting.current = false

          return
        }

        const machineFormData = packagingStore.getFormData(
          oldest.deptId,
          oldest.machineId
        )
        if (!machineFormData) {
          packagingStore.dequeOldestSubmission()
          isSubmitting.current = false

          return
        }

        productFormData = machineFormData.products.find(
          (prod) => prod.id === oldest.productId
        )
        if (!productFormData) {
          packagingStore.dequeOldestSubmission()
          isSubmitting.current = false

          return
        }

        await submitSheet(productFormData, {
          deptId: oldest.deptId,
          machineId: oldest.machineId,
          sectionId: oldest.sectionId,
        })

        packagingStore.dequeOldestSubmission()
        isSubmitting.current = false
      } catch (error) {
        retryCount.current += 1
        isSubmitting.current = false
        console.error(error)

        if (retryCount.current >= MAX_RETRIES) {
          posthog.captureException(error, {
            data: productFormData,
            location: "sync",
          })
        }
      }
    },
    isEnabled ? 1000 : null
  )
}

export interface DeleteShiftParams {
  departmentId: string
  machineId: string
  productId: string
  skuId: string
  market: string
}

export async function deleteProductSheet(params: DeleteShiftParams) {
  params.market = params.market.toUpperCase()

  return api.post("packaging/v3/sheet/delete", {
    json: params,
  })
}

export interface PackagingHistory {
  sheetId: string
  productName: string
  machineName: string
  departmentName: string
  employeeName: string
  shiftName: string
  shiftStartTime: string
  shiftEndTime: string
  status: string
  lastUpdatedAtIST: string
  parameters: HistoryParam[]
}

export interface HistoryParam {
  type: PackagingParamSchemaType
  list: HistoryListItem[]
}

export interface HistoryListItem {
  name: string
  value: MarketType
  inputTypeDomestic: ParamSchemaType | null
  inputTypeExport: ParamSchemaType | null
  labelDomestic: string | null
  labelExport: string | null
  unitDomestic: string | null
  unitExport: string | null
}

export interface GetPrevHistoryRes {
  sheets: PackagingHistory[]
}

export async function getPrevHistory(deptId: string): Promise<DeptHistory> {
  const res = await api
    .get<GetPrevHistoryRes>(`packaging/${deptId}/last-history`)
    .json()

  const history: DeptHistory = {
    id: deptId,
    sheets: res.sheets,
  }

  const store = useStore.getState()
  store.packaging.addHistory(history)

  return history
}
